/**
 * The behavior-event vocabulary (VH-2669).
 *
 * Mirrors the server enums in `app/Enums/Behavior*.php`. The server rejects
 * anything it does not recognise, so a value added here without its server
 * counterpart produces a 422 rather than a silently-dropped event — noisy in
 * the right direction, but still a drift worth avoiding. The pinned list in
 * `__tests__/contract.test.ts` makes a unilateral change here fail loudly;
 * read that file for what it does and does not actually guarantee.
 */

// The const-object + same-named-type pattern is deliberate: it gives call sites
// one name for both the values and the union. no-redeclare cannot see that these
// occupy different declaration spaces, so it is disabled per pair rather than
// renaming the types and making every import site say ...Value.
export const BehaviorEventName = {
  ScreenViewed: 'screen_viewed',
  ActionStarted: 'action_started',

  // An attempt already under way reached an intermediate step (VH-2811).
  // `metadata.step` names it; `durationMs`, where given, is how long the phase
  // that just finished took. Before this existed, intermediate steps rode on
  // action_started and were counted as extra starts.
  ActionProgressed: 'action_progressed',
  ActionCompleted: 'action_completed',
  ActionAbandoned: 'action_abandoned',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BehaviorEventName = typeof BehaviorEventName[keyof typeof BehaviorEventName]

/**
 * Where an event happened.
 *
 * **Never derive one of these from a route path.** The IVD routes carry
 * `:provider_uuid`, `:patient_uuid` and `:plan_uuid`, so anything built from
 * `route.path` or `route.fullPath` puts patient identifiers into a table
 * designed to hold none. Screens come from the route's `meta.screen`, and a
 * route without one emits nothing at all.
 */
export const BehaviorScreen = {
  IvVerify: 'iv_verify',

  // VH-2583's Insurance Inbox, added 16 Sep while this instrumentation was in
  // flight and merged in without a screen. Until VH-2811 it emitted nothing,
  // and activeScreen() was null while a user was on it.
  IvInbox: 'iv_inbox',
  IvBulk: 'iv_bulk',
  IvBulkNew: 'iv_bulk_new',
  IvBulkDetail: 'iv_bulk_detail',
  IvPriceList: 'iv_price_list',
  IvHistory: 'iv_history',
  IvHistoryDetail: 'iv_history_detail',
  IvPatientDetail: 'iv_patient_detail',
  IvPlanWorkspace: 'iv_plan_workspace',
  IvSettings: 'iv_settings',
  ProviderReports: 'provider_reports',
  ProviderValueDashboard: 'provider_value_dashboard',
  IvInvestigationDetail: 'iv_investigation_detail',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BehaviorScreen = typeof BehaviorScreen[keyof typeof BehaviorScreen]

/** The multi-step action a funnel event belongs to. */
export const BehaviorAction = {
  PatientSearch: 'patient_search',
  VerificationRun: 'verification_run',
  DiscoveryRun: 'discovery_run',
  CoverageRecovery: 'coverage_recovery',
  InvestigationRequest: 'investigation_request',
  BulkUpload: 'bulk_upload',

  // Funnel 1's final step (VH-2811): what a user does with a result once it is
  // on screen. Its own action so follow-ups cannot corrupt verification_run.
  ResultFollowUp: 'result_follow_up',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BehaviorAction = typeof BehaviorAction[keyof typeof BehaviorAction]

/**
 * What each action's metadata may contain.
 *
 * Every value is a literal union, never `string`. That is the type-level half
 * of the epic's PHI guarantee, and the looseness worth guarding against is not
 * `{ ssn: patient.ssn }` — nobody writes that. It is `{ payerName: plan.name }`
 * a year from now, which type-checks fine against `string` and puts patient
 * insurance information in the event stream.
 *
 * The server enforces the same allow-list independently
 * (`BehaviorEventMetadataAllowList`), so these types are the fast feedback,
 * not the security boundary. VH-2671 adds the ESLint rule that also rejects
 * non-literal *expressions* at these positions, which types alone cannot do.
 */
export interface BehaviorMetadataByAction {

  /**
   * The lookup (VH-2865): one attempt per pass through "Look up a patient",
   * from the entry form to a result. `with_card` rides on the start, `step` is
   * where the flow went, and `result` is how it reached a result: already
   * verified this month (`on_file`), a verify that succeeded, a discovery or a
   * recovery. See useLookupAttempt.
   */
  [BehaviorAction.PatientSearch]: {
    with_card?: 'yes' | 'no'
    step?: 'disambiguating' | 'plans' | 'no_coverage' | 'discovering' | 'recovery' | 'no_results'
    result?: 'on_file' | 'verified' | 'discovered' | 'recovered'
  }

  /**
   * `result` is the *session* outcome — whether our call to the payer
   * completed — and matches VerificationSession/DiscoverySession.status
   * exactly. It is deliberately not the coverage outcome.
   *
   * Two reasons. The session status describes our own interaction with the
   * payer's API, which is the same reasoning IvInvestigationEntryPoint gives
   * for why an entry point is safe to put on a Notion card. Whether a
   * particular patient turned out to have active coverage is a fact about
   * that patient, and does not belong on a per-user, per-timestamp row.
   *
   * There is no `entry` key: the event already carries the screen it was
   * emitted from, which is the same information.
   */
  [BehaviorAction.VerificationRun]: {
    result?: 'success' | 'failed' | 'incomplete'
  }

  /**
   * Discovery reports a different vocabulary from verification —
   * DiscoverySession.status is 'pending' | 'complete' | 'error'. 'pending' is
   * absent on purpose: a pending run has not finished, and the call site does
   * not emit a completion until it has.
   */
  [BehaviorAction.DiscoveryRun]: {
    result?: 'complete' | 'error'
  }
  [BehaviorAction.CoverageRecovery]: {
    outcome?: 'plans' | 'discover' | 'dead_end'
    polled_discovery?: 'yes' | 'no'

    /**
     * A step of the attempt, on action_progressed: a phase that just finished,
     * with its duration, or a pause for the user to pick a chart (`patients`),
     * with none — the same attempt continues once they answer (VH-2811).
     */
    step?: 'patients' | 'discovery_polling'
  }
  [BehaviorAction.InvestigationRequest]: {
    entry_point?:
      | 'plan_found_not_verified'
      | 'secondary_sub_check'
      | 'discovery_error'
      | 'no_coverage_found'
      | 'temporary_payer_issue'
    step?: 'affordance_shown' | 'dialog_opened' | 'submitted' | 'dismissed'
  }
  [BehaviorAction.BulkUpload]: {
    row_count?: 'under_100' | 'hundreds' | 'thousands'
  }
  [BehaviorAction.ResultFollowUp]: {
    kind?:
      | 'pdf_downloaded'
      | 'additional_plans_searched'
      | 'assistant_asked'
      | 'coordination_of_benefits'
      | 'summary_regenerated'
  }
}

/** One event as the ingest endpoint expects it. */
export interface BehaviorEventPayload {
  client_event_id: string
  event_name: BehaviorEventName
  screen: BehaviorScreen
  action: BehaviorAction | null
  session_id: string

  // The funnel attempt this event belongs to (VH-2811). Null on screen views,
  // which belong to none; a session holds many attempts, so session_id cannot
  // stand in for it.
  attempt_id: string | null
  occurred_at: string
  duration_ms: number | null
  metadata: Record<string, string> | null
}
